import {useReposAppPayload} from '@github-ui/code-view-shared/contexts/FilesPageInfoContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {type CoreRefInfo, useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {Blame, CodeOwnerInfo} from '@github-ui/code-view-types'
import {type Repository, useCurrentRepository} from '@github-ui/current-repository'
import {blamePath, blobPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {QuestionIcon, ShieldLockIcon} from '@primer/octicons-react'
import {Link as PrimerLink, Truncate} from '@primer/react'
import {Octicon, Tooltip} from '@primer/react/deprecated'
import {clsx} from 'clsx'

import {useDeferredMetadata} from '../../../contexts/DeferredMetadataContext'
import {useCurrentBlame} from '../../../hooks/CurrentBlame'
import {useCurrentBlob} from '../../../hooks/CurrentBlob'
import styles from './CodeSizeDetails.module.css'

export default function CodeSizeDetails({className}: {className?: string}) {
  const {codeownerInfo} = useDeferredMetadata()
  const {helpUrl} = useReposAppPayload()
  const payload = useCurrentBlob()
  const path = usePath()
  const refInfo = useRefInfo()
  const repo = useCurrentRepository()
  const blame = useCurrentBlame()

  const {
    headerInfo: {
      blobSize,
      isGitLfs,
      lineInfo: {truncatedLoc, truncatedSloc},
      mode,
    },
    viewable,
    rawLines,
  } = payload

  const symlinkUrl = mode === 'symbolic link' ? getSymlinkUrl({rawLines, blame, repo, refInfo, path}) : undefined

  return (
    <div className={clsx(className, styles.Box)}>
      <div className={clsx('text-mono', styles.Box_1)}>
        {mode !== 'file' && !symlinkUrl && (
          <>
            <Truncate title={mode} inline className={styles.Truncate}>
              <span>{mode}</span>
            </Truncate>
            {viewable && <span className={styles.Text}>·</span>}
          </>
        )}
        {viewable ? (
          <>
            {symlinkUrl && (
              <>
                <PrimerLink as={Link} muted to={symlinkUrl} className={styles.PrimerLink}>
                  Symbolic Link
                </PrimerLink>
                <span className={styles.Text}>·</span>
              </>
            )}
            <Truncate maxWidth="100%" title={blobSize} inline data-testid="blob-size" className={styles.Truncate_1}>
              <span>{`${truncatedLoc} lines (${truncatedSloc} loc) · ${blobSize}`}</span>
            </Truncate>
          </>
        ) : (
          <span>{blobSize}</span>
        )}
        {codeownerInfo && <CodeOwnersBadge codeownerInfo={codeownerInfo} />}
        {isGitLfs && (
          <>
            <span className="file-info-divider" />
            <PrimerLink
              muted
              href={`${helpUrl}/articles/versioning-large-files/`}
              aria-label="Learn more about Git LFS"
              className={styles.PrimerLink_1}
            >
              <Octicon icon={QuestionIcon} />
            </PrimerLink>
            <span> Stored with Git LFS</span>
          </>
        )}
      </div>
    </div>
  )
}

function CodeOwnersBadge({
  codeownerInfo: {codeownerPath, ownedByCurrentUser, ownersForFile, ruleForPathLine},
}: {
  codeownerInfo: CodeOwnerInfo
}) {
  if (!(ownedByCurrentUser || ownersForFile)) {
    return null
  }
  const tooltipAriaLabel = getCodeownersText(ownedByCurrentUser, ownersForFile, ruleForPathLine)

  return (
    <>
      <span className={styles.Text_1}> · </span>
      <Tooltip id="codeowners-tooltip" aria-label={tooltipAriaLabel} wrap className={styles.Tooltip}>
        {codeownerPath ? (
          <PrimerLink
            aria-labelledby="codeowners-tooltip"
            href={codeownerPath}
            muted={!ownedByCurrentUser}
            className={ownedByCurrentUser ? styles.codeOwnerAccent : undefined}
          >
            <Octicon icon={ShieldLockIcon} />
          </PrimerLink>
        ) : (
          <Octicon icon={ShieldLockIcon} className={ownedByCurrentUser ? styles.codeOwnerAccent : undefined} />
        )}
      </Tooltip>
    </>
  )
}

function getCodeownersText(
  ownedByCurrentUser: boolean | null,
  ownersForFile: string | null,
  ruleForPathLine: string | null,
) {
  let tooltipAriaLabel = 'Owned by '
  if (ownedByCurrentUser) {
    tooltipAriaLabel += 'you'
    if (ownersForFile) {
      tooltipAriaLabel += ' along with '
    }
  }
  tooltipAriaLabel += ownersForFile
  if (ruleForPathLine) {
    tooltipAriaLabel += ` (from CODEOWNERS line ${ruleForPathLine})`
  }

  return tooltipAriaLabel
}

function getSymlinkUrl({
  rawLines,
  blame,
  repo,
  refInfo,
  path: currentPath,
}: {
  rawLines: string[] | null
  blame: Blame | undefined
  repo: Repository
  refInfo: CoreRefInfo
  path: string
}) {
  if (!rawLines || !rawLines[0]) {
    return null
  }

  // if the symlink is absolute, we just use it from the root of the repo (ie - `foo/bar`)
  // if the symlink is relative, we need to combine it with the current path to build the right path to the linked file
  // e.g. if the symlink is `../foo/bar` and the current path is `baz/current_file`, the url should be `baz/../foo/bar`
  // this logic makes sure that we don't relative path out of base blob `[nwo]/[blob]/[ref]` url
  let filePath = rawLines[0]

  // this is a best-effort attempt at determining whether the symlink is relative. if it starts with `/`, it's absolute.
  // if it matches the format of a windows-style absolute path (`C:\foo`), consider it absolute. otherwise, consider
  // it relative.
  if (!filePath.startsWith('/') && !/^[a-zA-Z]:\\/.test(filePath)) {
    // needs an extra ../ to get to the current file's directory where the symlink was created
    filePath = `${currentPath}/../${filePath}`
  }

  const params = {
    owner: repo.ownerLogin,
    repo: repo.name,
    commitish: refInfo.name,
    filePath,
  }

  return blame ? blamePath(params) : blobPath(params)
}
